import {
  Badge,
  Box,
  Button,
  Card,
  Container,
  Grid,
  Group,
  Image,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  rgba,
  useMantineTheme,
} from "@mantine/core";
import {
  IconArrowRight,
  IconBook,
  IconBrandWhatsapp,
  IconClock,
  IconHeart,
  IconShieldCheck,
  IconUsers,
  IconWorld,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import image2 from "../assets/images/2.jpg";
import image3 from "../assets/images/3.jpg";
import image4 from "../assets/images/4.jpg";
import { buildWhatsAppUrl } from "../Services/contact.service";
import Seo from "../seo/Seo";
import {
  buildCanonicalUrl,
  createBreadcrumbSchema,
  createOrganizationSchema,
  createWebsiteSchema,
} from "../seo/utils";
import { assignmentCategories } from "../data/assignmentHelpCategories";
import "./AboutUs.css";
import { HugeTitle } from "./Shared/HugeTitle";
import SectionHeader from "./Shared/SectionHeader";

function AboutUs() {
  const theme = useMantineTheme();
  const whatsappUrl = buildWhatsAppUrl(
    "Hi SubjectBuddy, I would like to learn more about your tutoring support.",
  );

  const highlights = [
    {
      value: "Global Reach",
      label: "Personally supporting learners across time zones and study systems.",
    },
    {
      value: "1-to-1 Help",
      label: "Human tutors who explain clearly, patiently, and in your style.",
    },
    {
      value: "Learn + Teach",
      label: "Built by people who genuinely love learning and sharing knowledge.",
    },
  ];

  const reasons = [
    {
      icon: IconHeart,
      title: "Personal help, not generic replies",
      description:
        "Every student gets real human guidance shaped around their subject, pace, and confidence level.",
      color: theme.colors.green[6],
    },
    {
      icon: IconWorld,
      title: "Support for students around the globe",
      description:
        "We work with learners from different countries and academic systems, making tutoring accessible wherever you study.",
      color: theme.colors.teal[5],
    },
    {
      icon: IconBook,
      title: "Teaching that builds understanding",
      description:
        "We care about helping you actually learn the topic, not just move past the next deadline.",
      color: theme.colors.lime[6],
    },
    {
      icon: IconClock,
      title: "Dependable when time feels tight",
      description:
        "From urgent clarifications to longer-term study guidance, we stay responsive and practical.",
      color: theme.colors.green[8],
    },
  ];

  const principles = [
    {
      icon: IconUsers,
      title: "Student-centred tutoring",
      description:
        "We listen first, understand where you are stuck, and then teach in a way that feels manageable.",
    },
    {
      icon: IconShieldCheck,
      title: "Ethical academic support",
      description:
        "Our approach is rooted in explanation, mentoring, and skill-building so your own ability keeps growing.",
    },
    {
      icon: IconHeart,
      title: "Care that feels human",
      description:
        "We know study pressure can be overwhelming, so we aim to make every interaction calm, supportive, and clear.",
    },
  ];

  return (
    <>
      <Seo
        title="About SubjectBuddy | Personal Tutors Who Love to Learn and Teach"
        description="Learn about SubjectBuddy, a global tutoring team that personally helps students through human-led, ethical, and confidence-building academic support."
        path="/about-us"
        schema={[
          createOrganizationSchema(),
          createWebsiteSchema(),
          createBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "About Us", path: "/about-us" },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "About SubjectBuddy",
            description:
              "SubjectBuddy offers personal tutoring, study support, and assignment help for students around the world.",
            url: buildCanonicalUrl("/about-us"),
          },
        ]}
      />

      <Box className="about-us-page">
        <Box className="about-hero">
          <Container size="xl">
            <Grid
              gutter={{ base: 40, md: 60 }}
              align="center"
              className="about-hero-grid"
            >
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Stack gap="xl" className="about-hero-copy">
                  <Badge
                    variant="light"
                    color="green"
                    radius="xl"
                    size="lg"
                    style={{ width: "fit-content", paddingInline: 16 }}
                  >
                    About SubjectBuddy
                  </Badge>

                  <Stack gap="md">
                    <Text
                      size="sm"
                      fw={700}
                      tt="uppercase"
                      c={theme.colors.teal[7]}
                      style={{ letterSpacing: "0.12em" }}
                    >
                      Personal tutoring with heart
                    </Text>
                    <HugeTitle
                      order={1}
                      style={{
                        fontSize: "clamp(2.6rem, 5vw, 4.8rem)",
                        lineHeight: 1.02,
                        letterSpacing: "-0.04em",
                        maxWidth: 700,
                      }}
                    >
                      We help students learn with confidence, clarity, and real human support.
                    </HugeTitle>
                    <Text size="lg" c="dimmed" maw={640} style={{ lineHeight: 1.8 }}>
                      SubjectBuddy is built by tutors who personally help people all around the
                      globe because we genuinely love to learn and teach. That love shapes how we
                      explain, mentor, and stay alongside students through every challenge, whether
                      they need assignment help, study support, or both.
                    </Text>
                  </Stack>

                  <Group>
                    <Button
                      size="lg"
                      radius="xl"
                      component="a"
                      href={whatsappUrl}
                      target="_blank"
                      rel="noreferrer"
                      leftSection={<IconBrandWhatsapp size={18} />}
                    >
                      Talk to a tutor
                    </Button>
                    <Button
                      size="lg"
                      radius="xl"
                      variant="default"
                      component={Link}
                      to="/blogs"
                      rightSection={<IconArrowRight size={18} />}
                    >
                      Explore student guides
                    </Button>
                  </Group>
                </Stack>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 6 }}>
                <Box className="about-hero-visual">
                  <Card
                    radius="xl"
                    p="lg"
                    className="about-hero-note"
                    style={{
                      background: rgba(theme.white, 0.88),
                      border: `1px solid ${rgba(theme.colors.green[6], 0.16)}`,
                      boxShadow: theme.shadows.lg,
                    }}
                  >
                    <Stack gap={6}>
                      <Text fw={700} c={theme.colors.green[8]}>
                        Human-first tutoring
                      </Text>
                      <Text size="sm" c="dimmed" style={{ lineHeight: 1.6 }}>
                        We do not treat students like tickets. We teach like mentors who care.
                      </Text>
                    </Stack>
                  </Card>

                  <Box className="about-hero-card about-hero-card-primary">
                    <Image src={image2} alt="SubjectBuddy tutors supporting students" />
                  </Box>

                  <Box className="about-hero-card about-hero-card-secondary">
                    <Image src={image3} alt="Students learning with SubjectBuddy support" />
                  </Box>
                </Box>
              </Grid.Col>
            </Grid>

            <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg" mt={50}>
              {highlights.map((highlight) => (
                <Card
                  key={highlight.value}
                  radius="xl"
                  p="xl"
                  style={{
                    background: rgba(theme.white, 0.8),
                    border: `1px solid ${rgba(theme.colors.green[6], 0.12)}`,
                    boxShadow: theme.shadows.sm,
                  }}
                >
                  <Stack gap={8}>
                    <Text fw={800} size="xl" c={theme.colors.green[8]}>
                      {highlight.value}
                    </Text>
                    <Text c="dimmed" style={{ lineHeight: 1.7 }}>
                      {highlight.label}
                    </Text>
                  </Stack>
                </Card>
              ))}
            </SimpleGrid>
          </Container>
        </Box>

        <Container size="xl" py={90}>
          <Grid gutter={{ base: 40, md: 60 }} align="center">
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Box className="about-section-shell">
                <Box className="about-story-image">
                  <Image src={image4} alt="Global online tutoring and mentoring" />
                </Box>
                <Card
                  radius="xl"
                  p="lg"
                  className="about-floating-badge"
                  style={{
                    background: rgba(theme.white, 0.92),
                    border: `1px solid ${rgba(theme.colors.teal[5], 0.18)}`,
                    boxShadow: theme.shadows.md,
                  }}
                >
                  <Group gap="sm" wrap="nowrap">
                    <ThemeIcon size={42} radius="xl" color="teal" variant="light">
                      <IconWorld size={20} />
                    </ThemeIcon>
                    <Stack gap={0}>
                      <Text fw={700}>Students worldwide</Text>
                      <Text size="sm" c="dimmed">
                        Different countries, same personal care.
                      </Text>
                    </Stack>
                  </Group>
                </Card>
              </Box>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 6 }}>
              <Stack gap="xl">
                <SectionHeader>
                  <HugeTitle style={{ fontSize: "2.8rem" }}>Our Mission</HugeTitle>
                </SectionHeader>
                <Stack gap="md">
                  <Text size="lg" style={{ lineHeight: 1.85 }} c="dimmed">
                    Our mission is simple: make learning feel more personal, more accessible, and
                    far less overwhelming. We know students do their best when someone explains with
                    patience, responds with care, and believes they can improve.
                  </Text>
                  <Text size="lg" style={{ lineHeight: 1.85 }} c="dimmed">
                    That is why SubjectBuddy exists. We personally help learners across the globe
                    with tutoring, study guidance, concept support, assignment help, and
                    encouragement that meets them where they are. We love learning ourselves, and
                    that is exactly why we love teaching others.
                  </Text>
                </Stack>
              </Stack>
            </Grid.Col>
          </Grid>
        </Container>

        <Container size="xl" pb={90}>
          <Card
            radius="2rem"
            p={{ base: "xl", md: "2.4rem" }}
            style={{
              background: `linear-gradient(180deg, ${rgba(theme.colors.green[0], 0.85)}, ${rgba(theme.colors.teal[0], 0.75)})`,
              border: `1px solid ${rgba(theme.colors.green[7], 0.1)}`,
              boxShadow: theme.shadows.sm,
            }}
          >
            <Stack gap="xl">
              <Box component="section" aria-labelledby="assignment-help-by-subject-heading">
                <Stack gap="xl">
                  <Stack gap={16} align="center">
                    <SectionHeader>
                      <HugeTitle
                        id="assignment-help-by-subject-heading"
                        component="h2"
                        style={{
                          fontSize: "2.5rem",
                          textAlign: "center",
                        }}
                      >
                        Assignment Help by Subject
                      </HugeTitle>
                    </SectionHeader>
                    <Text ta="center" c="dimmed" maw={820} style={{ lineHeight: 1.85 }}>
                      Explore subject-specific assignment help for business, law, management,
                      programming, data, computer science, and more. Choose your subject below to
                      find focused academic support, examples, and guidance for your coursework.
                    </Text>
                  </Stack>

                  <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                    {assignmentCategories.map((section, index) => (
                      <Card
                        key={section.title}
                        radius="xl"
                        p="xl"
                        style={{
                          background: theme.white,
                          borderTop: `4px solid ${
                            index % 3 === 0
                              ? theme.colors.green[6]
                              : index % 3 === 1
                                ? theme.colors.teal[5]
                                : theme.colors.lime[6]
                          }`,
                          boxShadow: theme.shadows.xs,
                        }}
                      >
                        <Stack gap="md">
                          <Text component="h3" fw={700} size="lg" c={theme.colors.dark[8]}>
                            {section.title}
                          </Text>
                          <Text c="dimmed" size="sm" style={{ lineHeight: 1.75 }}>
                            {section.description}
                          </Text>
                          <ul className="about-keyword-list">
                            {section.links.map((link) => (
                              <li key={link.slug} className="about-keyword-list-item">
                                <Link className="about-keyword-chip" to={link.href}>
                                  {link.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                          <Text size="sm" c={theme.colors.green[8]} fw={600}>
                            {section.cta}
                          </Text>
                        </Stack>
                      </Card>
                    ))}
                  </SimpleGrid>
                </Stack>
              </Box>
            </Stack>
          </Card>
        </Container>

        <Box className="about-values-band" py={90}>
          <Container size="xl">
            <Stack gap={16} align="center" mb={44}>
              <SectionHeader>
                <HugeTitle
                  style={{
                    fontSize: "2.6rem",
                    textAlign: "center",
                  }}
                >
                  Why Students Choose SubjectBuddy
                </HugeTitle>
              </SectionHeader>
              <Text ta="center" c="dimmed" maw={760} style={{ lineHeight: 1.8 }}>
                We bring together warmth, subject knowledge, and consistency so students feel
                supported by real tutors, not left alone with confusing material.
              </Text>
            </Stack>

            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
              {reasons.map((reason) => {
                const Icon = reason.icon;

                return (
                  <Card
                    key={reason.title}
                    radius="xl"
                    p="xl"
                    style={{
                      background: theme.white,
                      border: `1px solid ${rgba(reason.color, 0.12)}`,
                      boxShadow: `0 20px 45px ${rgba(reason.color, 0.12)}`,
                    }}
                  >
                    <Stack gap="md">
                      <ThemeIcon
                        size={56}
                        radius="xl"
                        variant="light"
                        color={reason.color}
                        style={{ background: rgba(reason.color, 0.12) }}
                      >
                        <Icon size={28} color={reason.color} />
                      </ThemeIcon>
                      <Text fw={700} size="xl" c={theme.colors.dark[8]}>
                        {reason.title}
                      </Text>
                      <Text c="dimmed" style={{ lineHeight: 1.8 }}>
                        {reason.description}
                      </Text>
                    </Stack>
                  </Card>
                );
              })}
            </SimpleGrid>
          </Container>
        </Box>

        <Container size="xl" py={90}>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={28}>
            <Card
              radius="xl"
              p="xl"
              style={{
                background: `linear-gradient(135deg, ${rgba(theme.colors.green[6], 0.08)}, ${rgba(theme.colors.teal[5], 0.12)})`,
                border: `1px solid ${rgba(theme.colors.green[7], 0.12)}`,
                minHeight: 280,
              }}
            >
              <Stack gap="lg">
                <Text
                  size="sm"
                  fw={700}
                  tt="uppercase"
                  c={theme.colors.teal[7]}
                  style={{ letterSpacing: "0.12em" }}
                >
                  Our Vision
                </Text>
                <Text
                  fw={800}
                  size="2rem"
                  c={theme.colors.green[9]}
                  style={{ lineHeight: 1.2 }}
                >
                  To become the trusted study partner students remember long after the exam is over.
                </Text>
                <Text c={theme.colors.dark[6]} style={{ lineHeight: 1.85 }}>
                  We want SubjectBuddy to be known for thoughtful tutoring that helps learners build
                  stronger habits, deeper understanding, and lasting confidence in their own
                  ability.
                </Text>
              </Stack>
            </Card>

            <Card
              radius="xl"
              p="xl"
              style={{
                background: theme.white,
                border: `1px solid ${rgba(theme.colors.dark[8], 0.08)}`,
                boxShadow: theme.shadows.sm,
                minHeight: 280,
              }}
            >
              <Stack gap="lg">
                <Text
                  size="sm"
                  fw={700}
                  tt="uppercase"
                  c={theme.colors.green[7]}
                  style={{ letterSpacing: "0.12em" }}
                >
                  Our Commitment
                </Text>
                <Text fw={800} size="2rem" c={theme.colors.dark[8]} style={{ lineHeight: 1.2 }}>
                  Teaching responsibly, supporting ethically, and helping every student grow.
                </Text>
                <Text c="dimmed" style={{ lineHeight: 1.85 }}>
                  We focus on guidance, explanation, and mentorship. Our goal is always to make the
                  learning process clearer and more manageable while respecting the student's own
                  journey and effort.
                </Text>
              </Stack>
            </Card>
          </SimpleGrid>
        </Container>

        <Box py={90} style={{ background: theme.white }}>
          <Container size="xl">
            <Stack gap={16} align="center" mb={44}>
              <SectionHeader>
                <HugeTitle
                  style={{
                    fontSize: "2.6rem",
                    textAlign: "center",
                  }}
                >
                  What Defines Us
                </HugeTitle>
              </SectionHeader>
              <Text ta="center" c="dimmed" maw={740} style={{ lineHeight: 1.8 }}>
                The way we work is shaped by the same values we hope students carry into their own
                academic lives: curiosity, consistency, and care.
              </Text>
            </Stack>

            <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
              {principles.map((principle, index) => {
                const Icon = principle.icon;
                const colors = [theme.colors.green[6], theme.colors.teal[5], theme.colors.lime[6]];
                const accent = colors[index % colors.length];

                return (
                  <Card
                    key={principle.title}
                    radius="xl"
                    p="xl"
                    style={{
                      borderTop: `5px solid ${accent}`,
                      background: "#fbfefc",
                    }}
                  >
                    <Stack gap="md">
                      <ThemeIcon size={52} radius="xl" variant="light" color={accent}>
                        <Icon size={24} color={accent} />
                      </ThemeIcon>
                      <Text fw={700} size="lg" c={theme.colors.dark[8]}>
                        {principle.title}
                      </Text>
                      <Text c="dimmed" style={{ lineHeight: 1.8 }}>
                        {principle.description}
                      </Text>
                    </Stack>
                  </Card>
                );
              })}
            </SimpleGrid>
          </Container>
        </Box>

        <Container size="lg" py={90}>
          <Card
            radius={32}
            p={{ base: "xl", md: "3rem" }}
            className="about-cta-band"
            style={{
              background: `linear-gradient(135deg, ${theme.colors.green[8]}, ${theme.colors.teal[6]})`,
              color: theme.white,
              boxShadow: `0 28px 60px ${rgba(theme.colors.green[9], 0.22)}`,
            }}
          >
            <Stack
              gap="lg"
              align="center"
              ta="center"
              style={{ position: "relative", zIndex: 1 }}
            >
              <Text
                size="sm"
                fw={700}
                tt="uppercase"
                style={{ letterSpacing: "0.14em", opacity: 0.85 }}
              >
                Learn with SubjectBuddy
              </Text>
              <Text fw={800} size="2.5rem" style={{ lineHeight: 1.15, maxWidth: 760 }}>
                If you want a tutor who teaches with patience and truly cares, we're here for you.
              </Text>
              <Text size="lg" style={{ lineHeight: 1.85, opacity: 0.92, maxWidth: 760 }}>
                Whether you need guidance for one topic or steady help through a full academic term,
                SubjectBuddy is ready to support your learning journey with study support,
                assignment help, or both.
              </Text>
              <Group>
                <Button
                  size="lg"
                  radius="xl"
                  variant="white"
                  c={theme.colors.green[9]}
                  component="a"
                  href={whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  leftSection={<IconBrandWhatsapp size={18} />}
                >
                  Start a conversation
                </Button>
                <Button
                  size="lg"
                  radius="xl"
                  variant="outline"
                  color="white"
                  component={Link}
                  to="/"
                >
                  Visit homepage
                </Button>
              </Group>
            </Stack>
          </Card>
        </Container>
      </Box>
    </>
  );
}

export default AboutUs;
